import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsersRound, Building2, Users, CheckCircle, Loader2, Plus, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flote/ui';
import { deliveryTsr } from '../../api';
import { TeamFormModal } from '../../components/teams/TeamFormModal';
import { getErrorMessage } from '../../lib/error-utils';
import type { CreateTeamRequest } from '@flote/contracts/http';

function StatCard({
  title,
  count,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-full p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-2xl font-bold">{count}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function TeamsPage() {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch teams list
  const { data: teamsResponse, isLoading: teamsLoading, refetch: refetchTeams } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-list'],
  });
  const teams = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Fetch hubs for reference
  const { data: hubsResponse } = deliveryTsr.adminHubs.list.useQuery({
    queryKey: ['hubs-list'],
  });
  const hubs = hubsResponse?.status === 200 ? hubsResponse.body : [];
  const hubsMap = new Map(hubs.map(h => [h.id, h]));

  // Fetch drivers for team assignment
  const { data: driversResponse } = deliveryTsr.adminDrivers.list.useQuery({
    queryKey: ['drivers-list'],
  });
  const drivers = driversResponse?.status === 200 ? driversResponse.body : [];

  // Mutations
  const createTeamMutation = deliveryTsr.adminTeams.create.useMutation();
  const syncTeamsMutation = deliveryTsr.adminTeams.sync.useMutation();

  // Compute stats
  const totalTeams = teams.length;
  const teamsWithHubs = teams.filter(t => t.hub !== null).length;
  const totalWorkers = teams.reduce((acc, t) => acc + (t.workerCount ?? 0), 0);
  const totalTasks = teams.reduce((acc, t) => acc + t.taskCount, 0);

  const handleCreateTeam = useCallback(async (data: CreateTeamRequest) => {
    setIsCreating(true);
    try {
      const response = await createTeamMutation.mutateAsync({
        body: data,
      });

      if (response.status === 201) {
        toast.success('Team created successfully');
        setIsCreateModalOpen(false);
        refetchTeams();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to create team');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create team'));
      console.error('Create team error:', error);
    } finally {
      setIsCreating(false);
    }
  }, [createTeamMutation, refetchTeams]);

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const response = await syncTeamsMutation.mutateAsync({
        body: {},
      });

      if (response.status === 200) {
        toast.success(`Synced ${response.body.synced} teams from Onfleet`);
        refetchTeams();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to sync teams');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync teams'));
      console.error('Sync teams error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [syncTeamsMutation, refetchTeams]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Teams</h1>
          <p className="text-muted-foreground">
            Manage delivery teams and their hub assignments
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={isSyncing}
            className="w-full sm:w-auto"
          >
            {isSyncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync from Onfleet
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Create Team
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Teams"
          count={totalTeams}
          icon={UsersRound}
          color="bg-blue-100 text-blue-600"
          isLoading={teamsLoading}
        />
        <StatCard
          title="Teams with Hubs"
          count={teamsWithHubs}
          icon={Building2}
          color="bg-green-100 text-green-600"
          isLoading={teamsLoading}
        />
        <StatCard
          title="Total Workers"
          count={totalWorkers}
          icon={Users}
          color="bg-purple-100 text-purple-600"
          isLoading={teamsLoading}
        />
        <StatCard
          title="Active Tasks"
          count={totalTasks}
          icon={CheckCircle}
          color="bg-orange-100 text-orange-600"
          isLoading={teamsLoading}
        />
      </div>

      {/* Teams List */}
      <Card>
        <CardHeader>
          <CardTitle>Team Roster</CardTitle>
        </CardHeader>
        <CardContent>
          {teamsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : teams.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <UsersRound className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No teams found</h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                Create your first team to organize drivers and manage deliveries.
              </p>
              <Button className="mt-4" onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Team
              </Button>
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden sm:table-cell">Hub</TableHead>
                      <TableHead>Workers</TableHead>
                      <TableHead className="hidden md:table-cell">Managers</TableHead>
                      <TableHead className="hidden sm:table-cell">Tasks</TableHead>
                      <TableHead className="hidden lg:table-cell">Self-Assignment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teams.map((team) => {
                      const hub = team.hub ? hubsMap.get(team.hub) : null;
                      return (
                        <TableRow
                          key={team.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/teams/${team.id}`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className="hidden h-8 w-8 items-center justify-center rounded-full bg-blue-100 sm:flex">
                                <UsersRound className="h-4 w-4 text-blue-600" />
                              </div>
                              <span className="font-medium">{team.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {hub ? (
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span>{hub.name}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={(team.workerCount ?? 0) > 0 ? 'default' : 'secondary'}>
                              {team.workerCount ?? 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge variant={(team.managerCount ?? 0) > 0 ? 'default' : 'outline'}>
                              {team.managerCount ?? 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Badge variant={team.taskCount > 0 ? 'default' : 'secondary'}>
                              {team.taskCount}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {team.enableSelfAssignment ? (
                              <Badge variant="default" className="bg-green-100 text-green-700">
                                <CheckCircle className="mr-1 h-3 w-3" />
                                Enabled
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">
                                <AlertCircle className="mr-1 h-3 w-3" />
                                Disabled
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Team Modal */}
      <TeamFormModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSubmit={handleCreateTeam}
        isProcessing={isCreating}
        hubs={hubs}
        drivers={drivers}
      />
    </div>
  );
}
